import { BG_START_SCALE_RATE, OPACITY_START_SCALE_RATE, RENDERER_MODE } from '@/constants';

import { defineVuexModule } from '../helpers';
import { Section } from '@/core/models/sections/section';

export default defineVuexModule({
  namespaced: true,
  state: {
    showMenu: false,
    showHiddenSeats: !RENDERER_MODE,
    showLabels: RENDERER_MODE,
    showLocks: false,
    bgImage: null,
    isBgUploaded: false,
    hasBgImage: false,
    bgImageScale: BG_START_SCALE_RATE,
    bgImageOpacity: OPACITY_START_SCALE_RATE,
    bgImageShowInRenderer: true,
    unsavedChanges: false,
    isPanning: false,
    isClickable: true,
    isDraggable: true,
    isResizing: false,
    resizedUnit: null as null | string,
    resizedUnitSmoothing: null,
    conflictCheckInProgress: false,
    panCoordinate: undefined as BoxLike | undefined,
    notification: '',
    notificationType: 'error',
    splitModeUnit: null as null | string,
  },

  /* eslint-disable no-shadow, max-len, no-param-reassign*/
  getters: {
    notificationType: (state) => (state.notificationType === 'error' ? state.notificationType : 'success'),
    notification: (state) => (state.notification !== '' ? state.notification : ''),
    inSplitMode: (state) => state.splitModeUnit !== null,
  },

  actions: {
    setShowHiddenSeats({ commit, state }, flag) {
      if (state.showHiddenSeats === flag) return;
      commit('showHiddenSeats', flag);
    },
    setShowLabels({ commit, state }, flag) {
      if (state.showLabels === flag) return;
      commit('showLabels', flag);
    },
    setShowLocks(context, flag) {
      if (context.state.showLocks === flag) return;
      context.commit('showLocks', flag);
    },
    updateBgImage(context, url) {
      context.commit('bgImage', url);
      context.commit('hasBgImage', !!url);
    },
    updateIsBgUploaded(context, flag) {
      context.commit('isBgUploaded', flag);
    },
    updateBgImageScale(context, scale) {
      context.commit('bgImageScale', scale);
    },
    updateBgImageOpacity(context, opacity) {
      context.commit('bgImageOpacity', opacity);
    },
    updateBgImageShowInRenderer(context, value) {
      context.commit('bgImageShowInRenderer', value);
    },
    showContextMenu(context) {
      context.dispatch('toggleContextMenu', true);
    },
    hideContextMenu(context) {
      context.dispatch('toggleContextMenu', false);
    },
    toggleContextMenu(context, flag) {
      context.commit('showMenu', flag);
    },
    setUnsavedChanges(context, flag) {
      context.commit('updateUnsavedChanges', flag);
    },
    setPanning(context, flag) {
      context.commit('updatePanning', flag);
    },
    updatePanCoordinate(context, flag) {
      context.commit('updatePanCoordinate', flag);
    },
    setDraggable(context, flag) {
      context.commit('updateDraggable', flag);
    },
    setResizing(context, flag) {
      context.commit('updateResizing', flag);
    },
    setResizedUnit(context, unit) {
      context.commit('updateResizedUnit', unit);
    },
    setResizedUnitSmoothing(context, unit) {
      context.commit('updateResizedUnitSmoothing', unit);
    },
    setClickable(context, flag) {
      context.commit('updateClickable', flag);
    },
    setConflictCheckStatus(context, flag) {
      context.commit('updateConflictCheckStatus', flag);
    },
    enableSplitMode(context, sid) {
      context.commit('enableSplitMode', sid);
    },
    disableSplitMode(context) {
      context.commit('disableSplitMode');
    },
  },

  mutations: {
    showHiddenSeats(state, value) {
      state.showHiddenSeats = value;
    },
    showLabels(state, value) {
      state.showLabels = value;
    },
    showLocks(state, value) {
      state.showLocks = value;
    },
    bgImage(state, value) {
      state.bgImage = value;
    },
    isBgUploaded(state, value) {
      state.isBgUploaded = value;
    },
    hasBgImage(state, value) {
      state.hasBgImage = value;
    },
    bgImageScale(state, value) {
      state.bgImageScale = value;
    },
    bgImageOpacity(state, value) {
      state.bgImageOpacity = value;
    },
    bgImageShowInRenderer(state, value) {
      state.bgImageShowInRenderer = value;
    },
    showMenu(state, value) {
      state.showMenu = value;
    },
    updateUnsavedChanges(state, value: boolean) {
      state.unsavedChanges = value;
    },
    updatePanning(state, value) {
      if (value) {
        state.isClickable = false;
      }
      state.isPanning = value;
    },
    updatePanCoordinate(state, value) {
      state.panCoordinate = value;
    },
    updateDraggable(state, value) {
      state.isDraggable = value;
    },
    updateResizing(state, value) {
      state.isResizing = value;
    },
    updateResizedUnit(state, value) {
      state.resizedUnit = value;
    },
    updateResizedUnitSmoothing(state, value) {
      state.resizedUnitSmoothing = value;
    },
    updateClickable(state, value) {
      state.isClickable = value;
    },
    updateConflictCheckStatus(state, value) {
      state.conflictCheckInProgress = value;
    },
    setMessage(state, msg) {
      state.notification = msg.message;
      state.notificationType = msg.type;
    },
    enableSplitMode(state, sid) {
      state.splitModeUnit = sid;
    },
    disableSplitMode(state) {
      state.splitModeUnit = null;
    },
  },
});
